const FIREBASE_APP_SCRIPT = 'https://www.gstatic.com/firebasejs/10.13.2/firebase-app-compat.js';
const FIREBASE_MESSAGING_SCRIPT = 'https://www.gstatic.com/firebasejs/10.13.2/firebase-messaging-compat.js';
const TOKEN_STORAGE_KEY = 'ticketing-fcm-token';
const DEVICE_STORAGE_KEY = 'ticketing-push-device-key';
const TOKEN_USER_STORAGE_KEY = 'ticketing-fcm-token-user';

function absoluteNotificationUrl() {
    const base = window.app && window.app.url ? window.app.url : window.location.origin;
    return base.replace(/\/$/, '') + '/favicon.ico';
}

function truncateNotificationLine(value, maxLength) {
    const text = String(value || '').replace(/\s+/g, ' ').trim();
    if (text.length <= maxLength) {
        return text;
    }
    return text.slice(0, maxLength - 1).trim() + '...';
}

function buildNotificationBody(senderName, messagePreview, ticketUnread, unreadLabel) {
    const sender = truncateNotificationLine(senderName || '', 80);
    const preview = truncateNotificationLine(messagePreview || '', 120);
    const messageLine = sender ? (sender + ': ' + preview) : preview;

    const lines = [];
    if (messageLine) {
        lines.push(messageLine);
    }
    if (ticketUnread > 1) {
        lines.push(ticketUnread + ' ' + (unreadLabel || 'new messages'));
    }

    return lines.join('\n');
}

function getNotificationVibratePattern(data) {
    const raw = data && data.vibrate_pattern ? String(data.vibrate_pattern) : '220,120,220';

    return raw
        .split(',')
        .map(function (value) {
            return parseInt(String(value).trim(), 10);
        })
        .filter(function (value) {
            return !Number.isNaN(value) && value >= 0;
        });
}

function buildNotificationTitle(data, fallbackTitle) {
    // Gaya WhatsApp: judul = nama percakapan (tiket), bukan nama aplikasi.
    if (data && data.subject_line) {
        return truncateNotificationText(data.subject_line, 80);
    }

    const appName = window.app && window.app.name ? window.app.name : 'Ticketing';
    const host = window.app && window.app.url ? new URL(window.app.url).host : window.location.host;

    return truncateNotificationText(fallbackTitle || (appName + ' - ' + host), 80);
}

function truncateNotificationText(value, maxLength) {
    const text = String(value || '').replace(/\s+/g, ' ').trim();
    if (text.length <= maxLength) {
        return text;
    }
    return text.slice(0, maxLength - 1).trim() + '...';
}

let initPromise = null;
let currentToken = localStorage.getItem(TOKEN_STORAGE_KEY);
let foregroundBound = false;
let lifecycleBound = false;
let permissionWatcherBound = false;
let activeVue = null;
let refreshTimer = null;
let retryTimer = null;
let syncing = false;

function isActiveAppTab() {
    return !document.hidden && document.visibilityState === 'visible';
}

function loadScript(src) {
    return new Promise((resolve, reject) => {
        const existing = document.querySelector('script[data-src="' + src + '"]');
        if (existing) {
            if (existing.dataset.loaded === '1') {
                resolve();
                return;
            }
            existing.addEventListener('load', () => resolve(), {once: true});
            existing.addEventListener('error', () => reject(new Error('Failed to load script: ' + src)), {once: true});
            return;
        }

        const script = document.createElement('script');
        script.src = src;
        script.async = true;
        script.dataset.src = src;
        script.addEventListener('load', function () {
            script.dataset.loaded = '1';
            resolve();
        }, {once: true});
        script.addEventListener('error', function () {
            reject(new Error('Failed to load script: ' + src));
        }, {once: true});
        document.head.appendChild(script);
    });
}

function getFcmConfig() {
    return window.app && window.app.fcm ? window.app.fcm : {};
}

export function isPushConfigured() {
    const config = getFcmConfig();
    return !!(config && config.enabled && config.apiKey && config.messagingSenderId && config.appId && config.projectId);
}

export function isPushSupported() {
    return !!(window.Notification && 'serviceWorker' in navigator);
}

export function getPushPermission() {
    return window.Notification ? window.Notification.permission : 'unsupported';
}

export function getDeviceKey() {
    let deviceKey = localStorage.getItem(DEVICE_STORAGE_KEY);
    if (deviceKey) {
        return deviceKey;
    }
    deviceKey = 'web-' + Math.random().toString(36).slice(2) + Date.now().toString(36);
    localStorage.setItem(DEVICE_STORAGE_KEY, deviceKey);
    return deviceKey;
}

// Identitas perangkat untuk API push: token FCM + device key. Dipakai
// mutation login saat menimpa sesi lama (SSO GCC / login ulang) untuk
// melepas push token milik akun sebelumnya dengan kredensial akun itu.
export function getStoredPushContext() {
    return {
        token: currentToken || localStorage.getItem(TOKEN_STORAGE_KEY) || '',
        device_key: isPushSupported() ? getDeviceKey() : '',
    };
}

async function ensureFirebase() {
    if (window.firebase && window.firebase.messaging) {
        return window.firebase;
    }

    await loadScript(FIREBASE_APP_SCRIPT);
    await loadScript(FIREBASE_MESSAGING_SCRIPT);
    return window.firebase;
}

async function ensureInitialized() {
    if (!isPushConfigured()) {
        return null;
    }

    if (initPromise) {
        return initPromise;
    }

    initPromise = (async () => {
        const firebase = await ensureFirebase();
        const config = getFcmConfig();

        if (!firebase.apps || firebase.apps.length < 1) {
            firebase.initializeApp({
                apiKey: config.apiKey,
                authDomain: config.authDomain,
                projectId: config.projectId,
                storageBucket: config.storageBucket,
                messagingSenderId: config.messagingSenderId,
                appId: config.appId,
                measurementId: config.measurementId,
            });
        }

        return firebase.messaging();
    })();

    return initPromise;
}

async function registerServiceWorker() {
    if (!('serviceWorker' in navigator) || !isPushConfigured()) {
        return null;
    }

    const config = getFcmConfig();
    const params = new URLSearchParams({
        apiKey: config.apiKey || '',
        authDomain: config.authDomain || '',
        projectId: config.projectId || '',
        storageBucket: config.storageBucket || '',
        messagingSenderId: config.messagingSenderId || '',
        appId: config.appId || '',
        measurementId: config.measurementId || '',
    });

    return navigator.serviceWorker.register('/firebase-messaging-sw.js?' + params.toString());
}

async function persistToken(token, vue) {
    if (!token) {
        return;
    }

    currentToken = token;
    localStorage.setItem(TOKEN_STORAGE_KEY, token);
    if (vue && vue.$store && vue.$store.state.user) {
        localStorage.setItem(TOKEN_USER_STORAGE_KEY, String(vue.$store.state.user.id));
    }
    await axios.post('api/push/register', {
        token: token,
        platform: 'web',
        device_key: getDeviceKey(),
    });
}

function clearRetryTimer() {
    if (retryTimer) {
        clearTimeout(retryTimer);
        retryTimer = null;
    }
}

function scheduleRetry() {
    if (retryTimer) {
        return;
    }

    retryTimer = setTimeout(function () {
        retryTimer = null;
        if (activeVue) {
            syncPushToken(activeVue, true).catch(function () {});
        }
    }, 60000);
}

function schedulePeriodicRefresh() {
    if (refreshTimer) {
        return;
    }

    refreshTimer = setInterval(function () {
        if (!document.hidden && activeVue) {
            syncPushToken(activeVue, true).catch(function () {});
        }
    }, 15 * 60 * 1000);
}

function getNotificationPayload(payload) {
    const notification = payload && payload.notification ? payload.notification : {};
    const data = payload && payload.data ? payload.data : {};

    return {
        title: buildNotificationTitle(data, notification.title || data.title || ''),
        body: truncateNotificationText(notification.body || data.body || '', 120),
        subjectLine: truncateNotificationText(data.subject_line || '', 120),
        messagePreview: truncateNotificationText(data.message_preview || notification.body || data.body || '', 120),
        messageFull: String(data.message_full || data.message_preview || notification.body || data.body || ''),
        senderName: data.sender_name || '',
        ticketUnread: parseInt(data.ticket_unread || '0', 10) || 0,
        unreadLabel: data.unread_label || '',
        icon: '',
        badge: absoluteNotificationUrl(),
        vibrate: getNotificationVibratePattern(data),
        tag: notification.tag || data.tag || 'ticket-notification',
        url: data.url || '/',
    };
}

async function showForegroundNotification(payload) {
    if (!window.Notification || window.Notification.permission !== 'granted') {
        return;
    }

    const message = getNotificationPayload(payload);

    try {
        if ('serviceWorker' in navigator) {
            const registration = await navigator.serviceWorker.getRegistration();
            if (registration && typeof registration.showNotification === 'function') {
                await registration.showNotification(message.title, {
                    body: buildNotificationBody(message.senderName, message.messagePreview, message.ticketUnread, message.unreadLabel),
                    badge: message.badge,
                    vibrate: message.vibrate,
                    silent: false,
                    timestamp: Date.now(),
                    tag: message.tag,
                    renotify: true,
                    data: {
                        url: message.url,
                    }
                });
                return;
            }
        }
    } catch (error) {
        // Fallback to Notification constructor below.
    }

    const notification = new window.Notification(message.title, {
        body: buildNotificationBody(message.senderName, message.messagePreview, message.ticketUnread, message.unreadLabel),
        badge: message.badge,
        vibrate: message.vibrate,
        silent: false,
        tag: message.tag,
    });
    notification.onclick = function () {
        window.focus();
        if (message.url) {
            window.location.href = message.url;
        }
        notification.close();
    };
}

function bindForegroundMessages(messaging, vue) {
    if (foregroundBound) {
        return;
    }

    messaging.onMessage(function (payload) {
        if (!vue || !vue.$store) {
            return;
        }

        if (isActiveAppTab()) {
            return;
        }

        vue.$store.commit('setNotificationUnreadTotal', (vue.$store.state.notificationUnreadTotal || 0) + 1);
        window.dispatchEvent(new CustomEvent('dashboard-refresh-notifications'));

        if (vue.$notify) {
            const message = getNotificationPayload(payload);
            vue.$notify({
                title: message.title,
                text: message.subjectLine ? (message.subjectLine + '<br>' + message.messagePreview) : message.messagePreview,
                type: 'info'
            });
        }

        showForegroundNotification(payload).catch(function () {});
    });

    foregroundBound = true;
}

function bindLifecycle(vue) {
    if (lifecycleBound) {
        return;
    }

    const refresh = function () {
        if (activeVue) {
            syncPushToken(activeVue, true).catch(function () {});
        }
    };

    window.addEventListener('focus', refresh);
    window.addEventListener('online', refresh);
    document.addEventListener('visibilitychange', function () {
        if (!document.hidden) {
            refresh();
        }
    });

    lifecycleBound = true;
    schedulePeriodicRefresh();
}

// Pantau perubahan izin notifikasi lewat Permissions API. Dengan ini,
// begitu user mengizinkan notifikasi (dari prompt browser, ikon gembok
// di address bar, maupun pengaturan situs browser), token FCM langsung
// tersambung otomatis tanpa harus manual reconnect di account settings.
function bindPermissionWatcher(vue) {
    if (permissionWatcherBound || !('permissions' in navigator)) {
        return;
    }

    navigator.permissions.query({name: 'notifications'}).then(function (status) {
        status.addEventListener('change', function () {
            if (status.state === 'granted' && (activeVue || vue)) {
                syncPushToken(activeVue || vue, true).catch(function () {});
            }
        });
        permissionWatcherBound = true;
    }).catch(function () {
        // Browser tidak mendukung permissions query — jalur lain tetap berlaku.
    });
}

export async function syncPushToken(vue, force) {
    activeVue = vue || activeVue;
    if (!activeVue || !activeVue.$store || !activeVue.$store.state.user || !isPushConfigured() || !isPushSupported()) {
        return null;
    }

    const permission = getPushPermission();
    if (permission === 'denied') {
        return null;
    }

    if (syncing) {
        return currentToken;
    }

    syncing = true;

    try {
        const messaging = await ensureInitialized();
        if (!messaging) {
            return null;
        }

        bindForegroundMessages(messaging, activeVue);
        bindLifecycle(activeVue);

        if (permission !== 'granted') {
            return null;
        }

        const registration = await registerServiceWorker();
        const config = getFcmConfig();
        const token = await messaging.getToken({
            vapidKey: config.vapidKey || undefined,
            serviceWorkerRegistration: registration || undefined,
        });

        if (!token) {
            scheduleRetry();
            return null;
        }

        // Daftarkan ulang juga saat user berganti (sesi ditimpa login baru,
        // mis. SSO dari GCC): token FCM yang sama harus pindah kepemilikan
        // ke akun baru di server, bukan tetap memantik notifikasi akun lama.
        const registeredUserId = localStorage.getItem(TOKEN_USER_STORAGE_KEY);
        const currentUserId = activeVue.$store.state.user ? String(activeVue.$store.state.user.id) : null;
        if (force || token !== currentToken || currentUserId !== registeredUserId) {
            await persistToken(token, activeVue);
        }

        clearRetryTimer();
        return token;
    } catch (error) {
        scheduleRetry();
        throw error;
    } finally {
        syncing = false;
    }
}

export async function initPushNotifications(vue) {
    if (!vue) {
        return;
    }

    activeVue = vue;

    if (!isPushConfigured() || !isPushSupported()) {
        return;
    }

    // Dipasang sebelum pengecekan izin agar user yang tadinya menolak lalu
    // mengizinkan dari pengaturan browser juga otomatis tersambung.
    bindPermissionWatcher(vue);

    if (getPushPermission() === 'denied') {
        return;
    }

    const messaging = await ensureInitialized();
    if (!messaging) {
        return;
    }

    bindForegroundMessages(messaging, vue);
    bindLifecycle(vue);

    if (getPushPermission() === 'granted') {
        await syncPushToken(vue, false);
    }
}

export async function requestPushPermission(vue) {
    if (!window.Notification || !isPushConfigured()) {
        return false;
    }

    bindPermissionWatcher(vue);

    const permission = await window.Notification.requestPermission();
    if (permission !== 'granted') {
        return false;
    }

    await syncPushToken(vue, true);
    return true;
}

export async function getPushStatus() {
    const configured = isPushConfigured();
    const supported = isPushSupported();
    const permission = getPushPermission();
    const deviceKey = supported ? getDeviceKey() : null;
    const token = currentToken || localStorage.getItem(TOKEN_STORAGE_KEY);

    const status = {
        configured: configured,
        supported: supported,
        permission: permission,
        registered: false,
        token_preview: token ? token.slice(0, 18) + '...' : null,
        device_key: deviceKey,
        last_used_at: null,
    };

    if (!configured || !supported || !window.axios || !window.axios.defaults.headers.common.Authorization) {
        return status;
    }

    try {
        const response = await axios.get('api/push/status', {
            params: {
                device_key: deviceKey,
            }
        });
        return Object.assign(status, response.data || {});
    } catch (error) {
        return status;
    }
}

export async function unregisterPushToken() {
    const token = currentToken || localStorage.getItem(TOKEN_STORAGE_KEY);
    const deviceKey = isPushSupported() ? getDeviceKey() : null;

    if (token && isPushConfigured()) {
        try {
            const messaging = await ensureInitialized();
            if (messaging && typeof messaging.deleteToken === 'function') {
                await messaging.deleteToken(token);
            }
        } catch (error) {
            // Ignore local token deletion errors and continue removing server state.
        }
    }

    if (token || deviceKey) {
        try {
            await axios.post('api/push/unregister', {
                token: token || '',
                device_key: deviceKey || '',
            });
        } catch (error) {
            // Ignore unregister errors on logout.
        }
    }

    currentToken = null;
    localStorage.removeItem(TOKEN_STORAGE_KEY);
    localStorage.removeItem(TOKEN_USER_STORAGE_KEY);
}



export async function getPushCapabilities() {
    const registration = 'serviceWorker' in navigator
        ? await navigator.serviceWorker.getRegistration().catch(function () {
            return null;
        })
        : null;

    return {
        secureContext: !!window.isSecureContext,
        serviceWorkerRegistered: !!registration,
        notificationPermission: getPushPermission(),
        supportsVibrate: typeof navigator.vibrate === 'function',
        supportsBadging: typeof navigator.setAppBadge === 'function' || typeof navigator.clearAppBadge === 'function',
    };
}
